import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { colors, radius } from '../theme';

const API = 'https://api.eighteen-media.id';

function hdr() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('sm_token')}` } };
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return ''; }
}

export default function Thumbnails() {
  const [artists, setArtists] = useState([]);
  const [fonts, setFonts] = useState([]);
  const [ugcProjects, setUgcProjects] = useState([]);
  const [sourceMode, setSourceMode] = useState('artist');
  const [artistId, setArtistId] = useState('');
  const [referenceUrl, setReferenceUrl] = useState('');
  const [uploadingRef, setUploadingRef] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [titleText, setTitleText] = useState('');
  const [fontStyle, setFontStyle] = useState('bebas');
  const [purpose, setPurpose] = useState('thumbnail');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [ugcJobId, setUgcJobId] = useState('');
  const [attachingUrl, setAttachingUrl] = useState('');
  const [attachedMsg, setAttachedMsg] = useState('');
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/artists`, hdr()).then(r => setArtists(r.data || [])).catch(() => {});
    axios.get(`${API}/api/thumbnails/fonts`, hdr()).then(r => setFonts(r.data || [])).catch(() => {});
    axios.get(`${API}/api/ugc/projects`, hdr()).then(r => {
      const list = r.data || [];
      setUgcProjects(list);
      if (list.length > 0) setUgcJobId(list[0].id);
    }).catch(() => {});
    loadHistory();
  }, []);

  async function loadHistory() {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/api/thumbnails`, hdr());
      setHistory(r.data || []);
    } catch (e) { /* diam saja, tidak kritis */ }
    setLoadingHistory(false);
  }

  const selectedArtist = artists.find(a => a.id === artistId);
  const selectedProject = ugcProjects.find(p => p.id === ugcJobId);

  async function uploadReference(file) {
    if (!file) return;
    setUploadingRef(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('reference', file);
      const r = await axios.post(`${API}/api/thumbnails/reference`, fd, {
        headers: { ...hdr().headers, 'Content-Type': 'multipart/form-data' },
      });
      setReferenceUrl(r.data.reference_url);
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal upload foto referensi');
    }
    setUploadingRef(false);
  }

  async function generate() {
    if (sourceMode === 'artist' && !artistId) { setError('Pilih artis dulu'); return; }
    if (sourceMode === 'upload' && !referenceUrl) { setError('Upload foto referensi dulu'); return; }
    if (!prompt.trim()) { setError('Isi prompt dulu'); return; }
    setError('');
    setAttachedMsg('');
    setGenerating(true);
    setResult(null);
    try {
      const body = { prompt: prompt.trim(), font_style: fontStyle };
      if (sourceMode === 'artist') body.artist_id = artistId;
      else body.reference_url = referenceUrl;
      if (purpose === 'thumbnail' && titleText.trim()) body.title_text = titleText.trim();

      const r = await axios.post(`${API}/api/thumbnails/generate`, body, hdr());
      setResult(r.data);
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal generate thumbnail');
    }
    setGenerating(false);
  }

  async function addVariation() {
    if (!result?.id) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/thumbnails/${result.id}/variation`, { font_style: fontStyle }, hdr());
      setResult(prev => ({ ...prev, generated_image_urls: r.data.generated_image_urls }));
      loadHistory();
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal generate variasi (hanya tersedia untuk sumber Artis)');
    }
    setGenerating(false);
  }

  async function attachTo(jobId, url) {
    if (!ugcJobId) { setError('Pilih proyek UGC tujuan dulu (di bagian atas) sebelum memasang'); return; }
    setAttachingUrl(url);
    setError('');
    setAttachedMsg('');
    try {
      await axios.post(`${API}/api/thumbnails/${jobId}/attach`, {
        selected_url: url, ugc_job_id: ugcJobId,
        target: purpose === 'ugc_bg' ? 'ugc_bg' : 'thumbnail',
      }, hdr());
      const label = selectedProject?.title || 'Proyek Kompilasi';
      setAttachedMsg((purpose === 'ugc_bg' ? 'Background' : 'Thumbnail') + ' berhasil dipasang ke "' + label + '". Buka menu Video panjang › Kompilasi untuk melihatnya.');
    } catch (e) {
      setError(e.response?.data?.error || 'Gagal attach');
    }
    setAttachingUrl('');
  }

  const canAttach = !!ugcJobId;

  async function downloadImage(url) {
    try {
      const res = await fetch(`${API}${url}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = url.split('/').pop() || 'thumbnail.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (e) {
      setError('Gagal mengunduh gambar');
    }
  }

  function ImageCard({ jobId, url }) {
    return (
      <div style={{ border: `1px solid ${colors.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
        <img src={`${API}${url}`} alt="" style={{ width: '100%', display: 'block', aspectRatio: '16/9', objectFit: 'cover' }} />
        <div style={{ padding: 8, display: 'flex', gap: 6 }}>
          <button onClick={() => attachTo(jobId, url)} disabled={!canAttach || attachingUrl === url} title={!canAttach ? 'Pilih proyek UGC tujuan dulu' : ''} style={{
            flex: 1, padding: '8px 0', borderRadius: 6, border: 'none',
            cursor: (!canAttach || attachingUrl === url) ? 'not-allowed' : 'pointer',
            background: canAttach ? colors.accent : colors.border,
            color: '#FFFFFF', fontSize: 12, fontWeight: 700,
          }}>{attachingUrl === url ? '...' : 'Pilih & Pasang'}</button>
          <button onClick={() => downloadImage(url)} style={{
            width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textPrimary, cursor: 'pointer',
          }}>
            <i className="ti ti-download" style={{ fontSize: 15 }}></i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: 40 }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="ti ti-photo-ai" style={{ fontSize: 22 }}></i>Thumbnail AI Generator
        </h1>
        <p style={{ fontSize: 13, color: colors.textSecondary, marginTop: 2 }}>
          Generate thumbnail atau background video dengan wajah artis konsisten via AI.
        </p>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', background: colors.dangerMuted, color: colors.danger, borderRadius: radius.md, marginBottom: 16, fontSize: 13 }}>{error}</div>
      )}
      {attachedMsg && (
        <div style={{ padding: '10px 14px', background: '#10b98122', color: '#10b981', borderRadius: radius.md, marginBottom: 16, fontSize: 13 }}>{attachedMsg}</div>
      )}

      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 20, marginBottom: 20 }}>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>1. Tujuan hasil</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'thumbnail', label: 'Thumbnail YouTube', icon: 'ti-brand-youtube' },
              { id: 'ugc_bg', label: 'Background Video UGC', icon: 'ti-movie' },
            ].map(p => (
              <button key={p.id} onClick={() => setPurpose(p.id)} style={{
                flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: `1px solid ${purpose === p.id ? colors.accent : colors.border}`,
                background: purpose === p.id ? colors.accent + '22' : colors.bgCardMuted,
                color: purpose === p.id ? colors.accent : colors.textSecondary,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <i className={`ti ${p.icon}`} style={{ fontSize: 15 }}></i>{p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14, padding: 12, background: colors.accent + '11', border: `1px solid ${colors.accent}55`, borderRadius: 8 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textPrimary, fontWeight: 700, marginBottom: 6 }}>2. Proyek UGC tujuan <span style={{ color: colors.danger }}>*</span></label>
          <select value={ugcJobId} onChange={e => setUgcJobId(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13, boxSizing: 'border-box' }}>
            <option value="">-- Pilih proyek UGC dulu --</option>
            {ugcProjects.map(p => (
              <option key={p.id} value={p.id}>{p.title || 'Proyek Kompilasi'}</option>
            ))}
          </select>
          <p style={{ fontSize: 11, color: colors.textSecondary, marginTop: 6 }}>
            Ini menentukan proyek mana yang akan menerima thumbnail/background yang kamu pilih nanti.
          </p>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>3. Sumber wajah</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'artist', label: 'Pakai Foto Artis', icon: 'ti-users-group' },
              { id: 'upload', label: 'Upload Foto Sendiri', icon: 'ti-upload' },
            ].map(s => (
              <button key={s.id} onClick={() => setSourceMode(s.id)} style={{
                flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                border: `1px solid ${sourceMode === s.id ? colors.accent : colors.border}`,
                background: sourceMode === s.id ? colors.accent + '22' : colors.bgCardMuted,
                color: sourceMode === s.id ? colors.accent : colors.textSecondary,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <i className={`ti ${s.icon}`} style={{ fontSize: 15 }}></i>{s.label}
              </button>
            ))}
          </div>
        </div>

        {sourceMode === 'artist' ? (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Artis <span style={{ color: colors.danger }}>*</span></label>
            <select value={artistId} onChange={e => setArtistId(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13 }}>
              <option value="">-- Pilih artis --</option>
              {artists.filter(a => a.avatar_url).map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
            {selectedArtist?.avatar_url && (
              <img src={`${API}${selectedArtist.avatar_url}`} alt="" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', marginTop: 8 }} />
            )}
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Upload foto referensi <span style={{ color: colors.danger }}>*</span></label>
            <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={e => uploadReference(e.target.files[0])} style={{ fontSize: 13, color: colors.textPrimary }} />
            {uploadingRef && <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 6 }}>Mengupload...</p>}
            {referenceUrl && !uploadingRef && (
              <img src={`${API}${referenceUrl}`} alt="" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'cover', marginTop: 8, display: 'block' }} />
            )}
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>4. Prompt / deskripsi adegan <span style={{ color: colors.danger }}>*</span></label>
          <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Contoh: artis berdiri di sisi kanan frame, di atas panggung konser dengan lampu neon ungu, gaya sinematik"
            style={{ width: '100%', minHeight: 70, padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>

        {purpose === 'thumbnail' && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Judul di thumbnail (opsional)</label>
            <input type="text" value={titleText} onChange={e => setTitleText(e.target.value)} placeholder="Contoh: Kompilasi Lagu Terbaik Shepin Mis"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bgCardMuted, color: colors.textPrimary, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
        )}

        {purpose === 'thumbnail' && titleText.trim() && (
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, color: colors.textSecondary, marginBottom: 6 }}>Gaya font</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {fonts.map(f => (
                <button key={f.key} onClick={() => setFontStyle(f.key)} style={{
                  padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  border: `1px solid ${fontStyle === f.key ? colors.accent : colors.border}`,
                  background: fontStyle === f.key ? colors.accent + '22' : colors.bgCardMuted,
                  color: fontStyle === f.key ? colors.accent : colors.textSecondary,
                }}>{f.label}</button>
              ))}
            </div>
          </div>
        )}

        <button onClick={generate} disabled={generating} className="btn btn-gold" style={{
          width: '100%', padding: '11px 0', fontSize: 14, fontWeight: 700,
          opacity: generating ? 0.5 : 1, cursor: generating ? 'not-allowed' : 'pointer',
        }}>
          {generating ? 'Generating...' : 'Generate Thumbnail'}
        </button>
      </div>

      {result && (
        <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 20, marginBottom: 20 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, marginBottom: 14 }}>Hasil generate baru</h3>
          {/* Sebagian gambar bisa gagal di Gemini (flaky); tanpa catatan ini user cuma lihat
              jumlahnya kurang tanpa tahu kenapa. */}
          {result.partial_note && (
            <div style={{ padding: '9px 12px', marginBottom: 12, borderRadius: 8, fontSize: 12.5,
              background: 'rgba(251,191,36,0.12)', color: '#D97706' }}>⚠️ {result.partial_note}</div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 16 }}>
            {result.generated_image_urls.map((url, i) => (
              <ImageCard key={i} jobId={result.id} url={url} />
            ))}
          </div>
          {sourceMode === 'artist' && (
            <button onClick={addVariation} disabled={generating} style={{
              padding: '9px 16px', borderRadius: 8, border: `1px solid ${colors.borderStrong}`, background: colors.bgCard,
              color: colors.textPrimary, fontSize: 13, fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer',
            }}>{generating ? '...' : '+1 Variasi'}</button>
          )}
        </div>
      )}

      <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: radius.lg, padding: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, marginBottom: 14 }}>Riwayat Generate</h3>
        {loadingHistory ? (
          <p style={{ fontSize: 13, color: colors.textSecondary }}>Memuat riwayat...</p>
        ) : history.length === 0 ? (
          <p style={{ fontSize: 13, color: colors.textSecondary }}>Belum ada riwayat generate.</p>
        ) : (
          history.map(job => (
            <div key={job.id} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${colors.border}` }}>
              <p style={{ fontSize: 12, color: colors.textSecondary, marginBottom: 8 }}>
                {fmtDate(job.created_at)} — "{job.prompt}"{job.title_text ? ` · Judul: "${job.title_text}"` : ''}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
                {(job.generated_image_urls || []).map((url, i) => (
                  <ImageCard key={i} jobId={job.id} url={url} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
